import { t } from '@stokr/components-library'
import type { Project } from 'models/project'
import type { CopyKey } from 'locales/copy-keys.gen'
import { getProjectStatusChip } from 'components/ui/investmentCard/projectCard'
import { useStoProject } from './useStoProject'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import { INVESTMENT_CARD_TYPE_LABEL_KEYS, isListedInvestmentType, type InvestmentType } from 'models/investment'
import { getProjectInvestmentType } from 'shared/domain/investOpportunities'

export interface HeroBreadcrumbItem {
  /** Copy key, resolved at render so it runs after initCopy(). */
  labelKey?: CopyKey
  /** Literal label, for the current project. */
  label?: string
  to?: string
}

export interface HeroIdentity {
  title: string
  description?: string
  ticker?: string
  /** Token icon candidates for the ticker chip, best first; the chip falls back on load error. */
  tickerIcons: string[]
  investmentType: InvestmentType
  typeLabelKey: CopyKey
  /** Same chip as the investment cards: back-office `frontendOverwrites`, or its defaults. */
  status: { label: string; color: string }
  breadcrumb: HeroBreadcrumbItem[]
}

/** Liquid registry icon — the fallback when the project has no logo. */
export const liquidAssetIconUrl = (assetId: string): string =>
  `https://liquid.network/api/v1/asset/${encodeURIComponent(assetId)}/icon`

/** Investment listing, filtered to this product category. */
const listingPath = (type: InvestmentType): string =>
  isListedInvestmentType(type) ? `/featured-assets?filter=${type}` : '/featured-assets'

export const buildHeroIdentity = (project: Project | undefined): HeroIdentity | null => {
  const title = project?.title?.trim() || project?.name?.trim()
  if (!project || !title) return null

  const investmentType = getProjectInvestmentType(project)
  // Same singular name as the investment cards' type chip.
  const typeLabelKey = INVESTMENT_CARD_TYPE_LABEL_KEYS[investmentType]

  return {
    title,
    description: project.excerpt?.trim() || project.description?.trim() || undefined,
    ticker: project.tokenSymbol?.trim() || undefined,
    tickerIcons: [
      project.logourl?.trim(),
      project.secondaryAssetId && liquidAssetIconUrl(project.secondaryAssetId),
    ].filter((url): url is string => Boolean(url)),
    investmentType,
    typeLabelKey,
    status: getProjectStatusChip(project.frontendOverwrites),
    breadcrumb: [
      { labelKey: 'stoPage.hero.breadcrumbInvest', to: '/featured-assets' },
      { labelKey: typeLabelKey, to: listingPath(investmentType) },
      { label: title },
    ],
  }
}

/**
 * The breadcrumb as schema.org `BreadcrumbList` JSON-LD, for search results. Each item links to its
 * listing; the page itself to `path`, its canonical. `<` escaped so the text cannot close the script.
 */
export const toBreadcrumbStructuredData = (breadcrumb: HeroBreadcrumbItem[], path: string): string =>
  JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumb.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.labelKey ? t(item.labelKey) : item.label,
      item: `https://stokr.io${item.to ?? path}`,
    })),
  }).replace(/</g, '\\u003c')

export function useHeroIdentity(): SectionState<HeroIdentity> {
  const { project, isPending, isError } = useStoProject()

  return resolveSectionState({
    isPending,
    isError,
    data: buildHeroIdentity(project) ?? undefined,
    isEmpty: () => false,
  })
}
